import React from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AdapterMomentJalaali } from '@mui/x-date-pickers/AdapterMomentJalaali';
import { AdapterMomentHijri } from '@mui/x-date-pickers/AdapterMomentHijri';
import moment from 'moment';
import momentJalaali from 'moment-jalaali';
import momentHijri from 'moment-hijri';
import 'moment/locale/fa';
import 'moment-hijri';
import 'moment-jalaali';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { useTranslation } from 'react-i18next';
import { Box, TextField, MenuItem, Grid } from '@mui/material';
import HijriDateDropdown from './HijriDateDropdown';

// Afghan Persian month names for Solar-Hijri calendar
const AF_JALALI_MONTHS = [
  'حمل', 'ثور', 'جوزا', 'سرطان', 'اسد', 'سنبله',
  'میزان', 'عقرب', 'قوس', 'جدی', 'دلو', 'حوت'
];

// Helper to compute number of days in a Hijri month (year, zero-based monthIndex)
const hijriDaysInMonth = (year, monthIdx) => {
  try {
    return momentHijri(`${year}/${monthIdx + 1}/1`, 'iYYYY/iM/iD').iDaysInMonth();
  } catch {
    return 30;
  }
};

/**
 * Localized date picker component that switches between Gregorian, Shamsi (Jalali),
 * and Qamari (Hijri) calendar systems.
 *
 * Props mirror those of MUI DatePicker + an `isRtl` flag for styling.
 */
const LocalizedDatePicker = ({
  value,
  onChange,
  label,
  isRtl = false,
  inputProps = {},
  name,
  ...rest
}) => {
  const { dateFormat } = useDateFormat();
  const { t } = useTranslation();

  // Pick adapter based on selected calendar format
  const Adapter = React.useMemo(() => {
    if (dateFormat === DATE_FORMATS.SHAMSI) return AdapterMomentJalaali;
    if (dateFormat === DATE_FORMATS.QAMARI) return AdapterMomentHijri;
    return AdapterMoment;
  }, [dateFormat]);

  // Ensure moment is in correct locale (fa for Jalali / Hijri)
  React.useEffect(() => {
    if (dateFormat !== DATE_FORMATS.GREGORIAN) {
      moment.locale('fa');
    } else {
      moment.locale('en');
    }
  }, [dateFormat]);

  // Convert string value (YYYY-MM-DD) to moment instance
  const momentValue = React.useMemo(() => {
    if (!value) return null;
    if (dateFormat === DATE_FORMATS.SHAMSI) {
      return momentJalaali(value, 'YYYY-MM-DD');
    }
    if (dateFormat === DATE_FORMATS.QAMARI) {
      return momentHijri(value, 'YYYY-MM-DD');
    }
    return moment(value, 'YYYY-MM-DD');
  }, [value, dateFormat]);

  // Provide safe minDate / maxDate to satisfy adapter constraints if not given by caller
  const computedMinDate = React.useMemo(() => {
    if (rest.minDate) return rest.minDate;
    if (dateFormat === DATE_FORMATS.SHAMSI) {
      return momentJalaali('1380-01-01', 'jYYYY-jMM-jDD'); // 2001-03-22
    }
    if (dateFormat === DATE_FORMATS.QAMARI) {
      return momentHijri('1400-01-01', 'iYYYY-iMM-iDD'); // ≈ 1979-11-20
    }
    return moment('1900-01-01');
  }, [rest.minDate, dateFormat]);

  const computedMaxDate = React.useMemo(() => {
    if (rest.maxDate) return rest.maxDate;
    if (dateFormat === DATE_FORMATS.QAMARI) {
      return momentHijri('1499-12-29', 'iYYYY-iMM-iDD'); // hijri adapter upper bound
    }
    if (dateFormat === DATE_FORMATS.SHAMSI) {
      return momentJalaali('1499-12-29', 'jYYYY-jMM-jDD'); // safe upper bound for Jalali
    }
    // For other calendars leave undefined to use library defaults
    return undefined;
  }, [rest.maxDate, dateFormat]);

  const handleChange = (newVal) => {
    const iso = newVal && newVal.isValid() ? newVal.clone().locale('en').format('YYYY-MM-DD') : '';
    if (typeof onChange === 'function') {
      onChange({ target: { name, value: iso } });
    }
  };

  // Hijri delegate to dedicated component
  if (dateFormat === DATE_FORMATS.QAMARI) {
    return (
      <HijriDateDropdown
        value={value}
        onChange={onChange}
        label={label}
        isRtl={isRtl}
        name={name}
      />
    );
  }

  // Jalali render dropdowns
  if (dateFormat === DATE_FORMATS.SHAMSI) {
    const m = momentValue || (dateFormat === DATE_FORMATS.SHAMSI ? momentJalaali() : momentHijri());
    let currentYear = dateFormat === DATE_FORMATS.SHAMSI ? m.jYear() : m.iYear();
    let currentMonth = dateFormat === DATE_FORMATS.SHAMSI ? m.jMonth() : m.iMonth();

    // Handle NaN edge cases
    if (isNaN(currentMonth) || currentMonth < 0) currentMonth = 0;

    let currentDay = dateFormat === DATE_FORMATS.SHAMSI ? m.jDate() : m.iDate();
    if (isNaN(currentDay) || currentDay < 1) currentDay = 1;

    // month names
    const monthNames = dateFormat === DATE_FORMATS.SHAMSI ? AF_JALALI_MONTHS : [
      'محرم', 'صفر', 'ربیع‌الاول', 'ربیع‌الثانی', 'جمادی‌الاول', 'جمادی‌الثانی',
      'رجب', 'شعبان', 'رمضان', 'شوال', 'ذوالقعده', 'ذوالحجه'
    ];

    // years range based on min/max
    const minY = dateFormat === DATE_FORMATS.SHAMSI ? (computedMinDate ? computedMinDate.jYear() : 1300) : (computedMinDate ? computedMinDate.iYear() : 1400);
    const maxY = dateFormat === DATE_FORMATS.SHAMSI
      ? (computedMaxDate ? computedMaxDate.jYear() : currentYear + 20)
      : (computedMaxDate ? computedMaxDate.iYear() : currentYear + 20);

    // Clamp year within available range
    currentYear = Math.min(Math.max(currentYear, minY), maxY);

    const years = [];
    for (let y = minY; y <= maxY; y++) years.push(y);

    // days in selected month/year
    const daysInMonth = dateFormat === DATE_FORMATS.SHAMSI
      ? momentJalaali.jDaysInMonth(currentYear, currentMonth + 1)
      : hijriDaysInMonth(currentYear, currentMonth);

    // Clamp month and day values
    const safeMonth = Math.min(Math.max(currentMonth, 0), monthNames.length - 1);
    currentDay = Math.min(Math.max(1, currentDay), daysInMonth);

    const handlePartChange = (part) => (e) => {
      let y = currentYear, mo = currentMonth, d = currentDay;
      const val = parseInt(e.target.value, 10);
      if (part === 'year') y = val;
      else if (part === 'month') mo = val;
      else d = val;

      // adjust day overflow
      const newMoment = dateFormat === DATE_FORMATS.SHAMSI
        ? momentJalaali().jYear(y).jMonth(mo).jDate(1)
        : momentHijri().iYear(y).iMonth(mo).iDate(1);

      const maxDay = dateFormat === DATE_FORMATS.SHAMSI
        ? momentJalaali.jDaysInMonth(y, mo + 1)
        : hijriDaysInMonth(y, mo);
      d = Math.min(d, maxDay);

      if (dateFormat === DATE_FORMATS.SHAMSI) {
        newMoment.jDate(d);
      } else {
        newMoment.iDate(d);
      }

      handleChange(newMoment);
    };

    return (
      <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
        {label && (
          <Box sx={{ mb: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Box>
        )}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <TextField select fullWidth size="small" value={currentDay} onChange={handlePartChange('day')}>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField select fullWidth size="small" value={safeMonth} onChange={handlePartChange('month')}>
              {monthNames.map((mName, idx) => (
                <MenuItem key={idx} value={idx}>{mName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField select fullWidth size="small" value={currentYear} onChange={handlePartChange('year')}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Gregorian calendar: keep popup picker
  return (
    <LocalizationProvider dateAdapter={Adapter} adapterLocale={dateFormat === DATE_FORMATS.GREGORIAN ? 'en' : 'fa'}>
      <DatePicker
        value={momentValue}
        onChange={handleChange}
        label={label}
        {...rest}
        minDate={computedMinDate}
        maxDate={computedMaxDate}
        slotProps={{
          textField: {
            fullWidth: true,
            ...inputProps,
            InputLabelProps: { shrink: true },
            dir: isRtl ? 'rtl' : 'ltr',
            sx: isRtl
              ? {
                  '& input': { textAlign: 'right', direction: 'rtl' },
                  '& label': { right: 25, left: 'auto', transformOrigin: 'top right' },
                }
              : {},
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default LocalizedDatePicker; 